import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import ToolCard from "../components/ToolCard";
import { TOOLS, TOOL_LABEL, TOOL_COLOR, TOOL_PATH, api } from "../lib/api";
import { Sparkles, ArrowDown, Calendar, Eye, Star } from "lucide-react";

const Home = () => {
  const [daily, setDaily] = useState(null);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/daily").then((r) => setDaily(r.data)).catch(() => {});
    api.get("/featured?limit=4").then((r) => setFeatured(r.data.rows || [])).catch(() => {});
  }, []);

  const dailyTool = daily?.tool;
  const dailyPrompt = daily?.prompt;
  const dailyColor = dailyTool ? TOOL_COLOR[dailyTool] : "#22C55E";

  return (
    <Layout>
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-12 sm:pt-20 pb-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 bg-white border-4 border-[#1a1a1a] rounded-full px-4 py-1.5 shadow-hard-sm mb-6 wobble">
              <Sparkles className="w-4 h-4 text-[#22c55e]" strokeWidth={3} />
              <span className="font-mono text-xs uppercase tracking-[0.25em] font-bold">
                {TOOLS.length} retos · IA · 100% gratis
              </span>
            </div>
            <h1 className="font-display font-black text-5xl sm:text-7xl md:text-[5.5rem] leading-[0.95] tracking-tight mb-6">
              retos creativos
              <br />
              <span className="chunky-underline">para destrabar</span>
              <br />
              <span className="text-[#22c55e]">tu cerebro.</span>
            </h1>
            <p className="font-mono text-base sm:text-lg text-[#4a4a4a] max-w-2xl leading-relaxed">
              Mini herramientas con IA para dibujar, escribir o simplemente reírse un rato.
              Elige una, y pasa al papel. Sin cuentas, sin ads, sin trampa.
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <div className="bg-[#22c55e] border-4 border-[#1a1a1a] rounded-3xl p-5 shadow-hard-md tilt-3 max-w-xs">
              <div className="font-display font-black text-xl leading-tight mb-2">¿cómo funciona?</div>
              <ol className="font-mono text-sm space-y-1.5 list-decimal list-inside">
                <li>elige un reto</li>
                <li>la ia te da el prompt</li>
                <li>tú lo dibujas o escribes</li>
                <li>repite hasta despeinarte</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* Daily Challenge */}
      {daily && dailyPrompt && (
        <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-12" data-testid="daily-section">
          <Link
            to={`/p/${dailyPrompt.id}`}
            data-testid="daily-banner"
            className="card-tilt block bg-[#1a1a1a] text-white border-4 border-[#1a1a1a] rounded-3xl p-7 sm:p-10 shadow-hard-lg relative overflow-hidden tilt-2"
          >
            <div
              className="absolute -top-4 -right-4 w-28 h-28 rounded-full border-4 border-[#1a1a1a] rotate-12"
              style={{ background: dailyColor }}
            />
            <div className="flex items-center gap-3 mb-4 relative">
              <div className="w-10 h-10 rounded-xl border-4 border-white bg-white text-[#1a1a1a] flex items-center justify-center">
                <Calendar className="w-5 h-5" strokeWidth={3} />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] font-bold text-white/60">reto del día · {daily.date}</div>
                <div className="font-display font-black text-xl">{TOOL_LABEL[dailyTool]}</div>
              </div>
            </div>
            <p className="font-display text-2xl sm:text-3xl leading-tight max-w-2xl mb-5 relative">
              "{(dailyPrompt.prompt || "").slice(0, 160)}{(dailyPrompt.prompt || "").length > 160 ? "..." : ""}"
            </p>
            <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] font-bold relative">
              <span style={{ color: dailyColor }}>jugar este reto</span> →
            </div>
          </Link>
        </section>
      )}

      <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-6">
        <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-[#737373] mb-6">
          <ArrowDown className="w-4 h-4" strokeWidth={3} /> elige tu reto
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-24" data-testid="tools-grid">
        <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8 sm:gap-10">
          {TOOLS.map((t, i) => (
            <ToolCard key={t.id} tool={t} index={i} />
          ))}
        </div>
      </section>

      {/* Top virales */}
      {featured.length > 0 && (
        <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-24" data-testid="viral-section">
          <div className="flex items-end justify-between gap-3 mb-6 flex-wrap">
            <div>
              <span className="inline-block font-mono text-xs uppercase tracking-[0.25em] font-bold bg-[#FFB000] border-4 border-[#1a1a1a] rounded-full px-4 py-1 mb-3">
                top virales
              </span>
              <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight">los más vistos.</h2>
            </div>
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#737373]">prueba social honesta · ranking en vivo</div>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {featured.map((p) => (
              <Link
                key={p.id}
                to={`/p/${p.id}`}
                data-testid={`viral-${p.id}`}
                className="card-tilt block bg-white border-4 border-[#1a1a1a] rounded-3xl shadow-hard-md p-5 sm:p-6 relative"
              >
                {p.featured && (
                  <div className="absolute -top-3 -right-3 bg-[#FFB000] border-4 border-[#1a1a1a] text-[#1a1a1a] rounded-full p-2 rotate-12">
                    <Star className="w-4 h-4 fill-current" strokeWidth={3} />
                  </div>
                )}
                <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                  <span
                    className="inline-block font-mono text-[10px] uppercase tracking-wider font-bold border-2 border-[#1a1a1a] rounded-full px-2 py-0.5"
                    style={{ background: TOOL_COLOR[p.tool] }}
                  >
                    {TOOL_LABEL[p.tool]}
                  </span>
                  <span className="inline-flex items-center gap-1 font-mono text-xs font-bold text-[#737373]">
                    <Eye className="w-3.5 h-3.5" strokeWidth={3} /> {p.views || 0}
                  </span>
                </div>
                <p className="font-display font-bold text-lg leading-snug line-clamp-3">{p.prompt}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-24">
        <div className="bg-[#1a1a1a] text-white border-4 border-[#1a1a1a] rounded-3xl p-8 sm:p-14 shadow-hard-lg relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#ec4899] border-4 border-white opacity-90" />
          <div className="absolute bottom-6 right-12 w-16 h-16 rounded-xl bg-[#ffb000] border-4 border-white rotate-12" />
          <h2 className="font-display font-black text-3xl sm:text-5xl leading-tight mb-4 max-w-xl">
            no necesitas talento.
            <br />
            necesitas <span className="text-[#22c55e]">empezar.</span>
          </h2>
          <p className="font-mono text-sm sm:text-base text-white/80 max-w-xl">
            Estas herramientas no califican tu dibujo. No te dan estrellitas. Solo te lanzan
            ideas raras para que la mano se mueva. Lo demás, lo pones tú.
          </p>
        </div>
      </section>
    </Layout>
  );
};

export default Home;
